/**
 * ConsoleLayout — the authenticated shell: dark sidebar (permanent on md+, a
 * temporary drawer on mobile) + sticky topbar + scrollable content (<Outlet/>).
 * Mirrors the prototype's web-native layout (sidebar instead of bottom tabs).
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Avatar, IconButton, Badge, Menu, MenuItem, ListItemIcon, Divider, Typography, Tooltip, Drawer,
} from '@mui/material';
import {
  NotificationsRounded, CalendarTodayRounded, LogoutRounded,
  PersonRounded, MenuRounded,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { colors, brand } from '../theme/theme';
import { NAV_ITEMS, SECTION_LABELS, pageTitleFor, NavItem } from './navConfig';
import { getDisplayName, getBusinessName, getRole, initials, clearSession, roleGradientKey } from '../auth/session';

const SIDEBAR_W = 256;
const TOPBAR_H = 68;
const SECTIONS = ['main', 'billing', 'account'] as const;

const ConsoleLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const displayName = getDisplayName();
  const businessName = getBusinessName();
  const role = getRole();
  const gradKey = roleGradientKey(role?.role_name);
  const avatarGrad = gradKey === 'provider' ? brand.gradProvider : gradKey === 'supervisor' ? brand.gradSupervisor : brand.gradAdmin;

  const today = useMemo(
    () => new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    []
  );

  const visibleItems = NAV_ITEMS.filter((it) => (it.visible ? it.visible() : true));
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => { clearSession(); navigate('/login', { replace: true }); };

  const navItem = (it: NavItem) => {
    const active = isActive(it.path);
    return (
      <Box
        key={it.path}
        component={Link}
        to={it.path}
        onClick={() => setDrawerOpen(false)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: '11px',
          color: active ? '#fff' : brand.sidebarFg, bgcolor: active ? brand.sidebarActive : 'transparent',
          fontWeight: 500, fontSize: 14, textDecoration: 'none', transition: 'background .12s, color .12s',
          '&:hover': { bgcolor: active ? brand.sidebarActive : 'rgba(255,255,255,.05)', color: '#fff' },
          '& svg': { fontSize: 21 },
        }}
      >
        {it.icon}
        <span>{it.label}</span>
      </Box>
    );
  };

  const sidebarInner = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, pb: 2.5, color: '#fff' }}>
        <Box component="img" src="/logo192.png" alt="BaseQueue" sx={{ width: 36, height: 36, borderRadius: '10px' }} />
        <Typography sx={{ fontWeight: 800, fontSize: 18 }}>BaseQueue</Typography>
      </Box>

      <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, overflowY: 'auto' }}>
        {SECTIONS.map((section) => {
          const items = visibleItems.filter((it) => it.section === section);
          if (!items.length) return null;
          const label = SECTION_LABELS[section];
          return (
            <React.Fragment key={section}>
              {label && (
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748B', px: 1.5, pt: 1.75, pb: 0.75, fontWeight: 700 }}>
                  {label}
                </Typography>
              )}
              {items.map(navItem)}
            </React.Fragment>
          );
        })}
      </Box>

      <Box sx={{ borderTop: '1px solid rgba(255,255,255,.08)', pt: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1 }}>
          <Avatar sx={{ width: 34, height: 34, fontSize: 13, background: brand.gradBrand }}>{initials(businessName)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{businessName}</Typography>
            {role?.role_display_name && <Typography sx={{ fontSize: 12, color: '#64748B' }}>{role.role_display_name}</Typography>}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: colors.backgroundDefault }}>
      {/* Permanent sidebar (md+) */}
      <Box component="aside" sx={{ width: SIDEBAR_W, flexShrink: 0, bgcolor: brand.sidebarBg, display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, height: '100vh' }}>
        {sidebarInner}
      </Box>

      {/* Mobile drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} sx={{ display: { md: 'none' } }}
        PaperProps={{ sx: { width: SIDEBAR_W, bgcolor: brand.sidebarBg, color: brand.sidebarFg } }}>
        {sidebarInner}
      </Drawer>

      {/* Main column */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box component="header" sx={{ height: TOPBAR_H, bgcolor: colors.backgroundPaper, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3.5 }, position: 'sticky', top: 0, zIndex: 20 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ display: { md: 'none' } }} aria-label="Open navigation"><MenuRounded /></IconButton>
            <Typography sx={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.2px' }}>{pageTitleFor(location.pathname)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, bgcolor: colors.gray50, border: `1px solid ${colors.border}`, px: 1.5, py: 1, borderRadius: '10px', color: colors.gray700, fontWeight: 600, fontSize: 13 }}>
              <CalendarTodayRounded sx={{ fontSize: 16 }} /> {today}
            </Box>
            <Tooltip title="Notifications">
              <IconButton sx={{ border: `1px solid ${colors.border}`, borderRadius: '10px' }} aria-label="Notifications">
                <Badge color="error" variant="dot"><NotificationsRounded sx={{ color: colors.gray600 }} /></Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title={displayName}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }} aria-label="Account menu">
                <Avatar sx={{ width: 38, height: 38, fontSize: 14, background: avatarGrad }}>{initials(displayName)}</Avatar>
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{displayName}</Typography>
                <Typography variant="caption" color="text.secondary">{role?.role_display_name || 'Provider'}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
                <ListItemIcon><PersonRounded fontSize="small" /></ListItemIcon>Profile & Settings
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: colors.error }}>
                <ListItemIcon><LogoutRounded fontSize="small" sx={{ color: colors.error }} /></ListItemIcon>Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box component="main" sx={{ p: { xs: 2, sm: 3.5 }, maxWidth: 1280, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsoleLayout;
